import React, { FC, memo, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { default as ReactMarkdown } from 'react-markdown';
import type { RootState } from '@@store/index';
import gfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import rehypeHighlight from 'rehype-highlight';
import 'katex/dist/katex.min.css';
import { withImageProxy } from '@/common/imageProxy';
import './react-markdown.less';

interface MarkdownPropsType {
    content: string;
    className?: string;
}

const CustomLink: FC<any> = ({ href, children, ...props }) => {
    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            {...props}
        >
            {children}
            <span className="link-focus-hint" aria-hidden="true"> (Opens a new tab)</span>
        </a>
    );
};

const CustomImage: FC<any> = ({ src, alt, ...props }) => {
    const proxySrc = withImageProxy(src);
    return <img src={proxySrc} alt={alt} loading="lazy" {...props} />;
};

const CopyButton: FC<{ text: string }> = ({ text }) => {
    const [ copied, setCopied ] = useState(false);

    const handleCopy = useCallback(() => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    }, [ text ]);

    return (
        <button
            className="code-copy-btn"
            onClick={handleCopy}
            title={copied ? 'Copied!' : 'Copy code'}
        >
            {copied ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                </svg>
            ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                </svg>
            )}
        </button>
    );
};

const CodeBlock: FC<any> = ({ node, inline, className, children, ...props }) => {
    const match = /language-(\w+)/.exec(className || '');

    // inline code: 没有 language class 且 inline 属性为 true（或没有 className）
    // react-markdown 对 `code` 在 <pre> 外时传 inline=true
    const isInline = inline || (!className && !String(children).includes('\n'));

    if (isInline) {
        return (
            <code className={className} {...props}>
                {children}
            </code>
        );
    }

    const codeText = String(children).replace(/\n$/, '');

    return (
        <div className="code-block-wrapper">
            <div className="code-block-header">
                {match && <span className="code-block-lang">{match[1]}</span>}
                <CopyButton text={codeText} />
            </div>
            <code className={className} {...props}>
                {children}
            </code>
        </div>
    );
};

const CustomTable: FC<any> = ({ children, ...props }) => (
    <div className="table-scroll-wrapper">
        <table {...props}>{children}</table>
    </div>
);

const components = {
    a: CustomLink,
    img: CustomImage,
    code: CodeBlock,
    table: CustomTable
};

const rehypePlugins: any[] = [
    rehypeRaw,
    rehypeKatex,
    [ rehypeHighlight, { detect: true, ignoreMissing: true } ]
];

const remarkPlugins = [ gfm, remarkMath ];

const MarkdownIndex: FC<MarkdownPropsType> = memo((props) => {
    const { content, className = '' } = props;
    const curTheme = useSelector((state: RootState) => state.theme);

    const processedContent = content?.trim() || '';

    if (!processedContent) return null;

    return (
        <div className={`base-markdown-react-markdown ${curTheme}-base-markdown-react-markdown ${className}`}>
            <ReactMarkdown
                remarkPlugins={remarkPlugins}
                rehypePlugins={rehypePlugins}
                components={components}
            >
                {processedContent}
            </ReactMarkdown>
        </div>
    );
});

export default MarkdownIndex;
